import { doc, getDoc, setDoc } from "firebase/firestore";
import { getFirebaseFirestore } from "@/config/firebase";
import { isDemoMode } from "@/demo/demoMode";

/**
 * 강사별 즐겨찾기(핀) 회원 ID 목록.
 *
 * 저장 경로: users/{instructorUid}/pinnedMembers/{centerId}
 * 문서 구조: { memberIds: string[] }
 *
 * 강사 자신의 uid 하위에 저장 (센터 소유자가 아닌 본인).
 * member collection에 기록하지 않음 — 강사 개인 데이터.
 */

function pinnedRef(instructorUid: string, centerId: number) {
  return doc(getFirebaseFirestore(), "users", instructorUid, "pinnedMembers", String(centerId));
}

const DEMO_KEY = "vlab:demo:pinnedMembers";

function loadDemoPinned(): Set<string> {
  try {
    const raw = localStorage.getItem(DEMO_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function saveDemoPinned(ids: Set<string>): void {
  try {
    localStorage.setItem(DEMO_KEY, JSON.stringify([...ids]));
  } catch {
    // ignore
  }
}

export async function getPinnedMembers(
  instructorUid: string,
  centerId: number,
): Promise<Set<string>> {
  if (isDemoMode()) return loadDemoPinned();

  const snap = await getDoc(pinnedRef(instructorUid, centerId));
  if (!snap.exists()) return new Set();
  const data = snap.data() as { memberIds?: string[] };
  return new Set(data.memberIds ?? []);
}

export async function togglePinnedMember(
  instructorUid: string,
  centerId: number,
  memberId: string,
): Promise<Set<string>> {
  if (isDemoMode()) {
    const current = loadDemoPinned();
    if (current.has(memberId)) {
      current.delete(memberId);
    } else {
      current.add(memberId);
    }
    saveDemoPinned(current);
    return current;
  }

  const ref = pinnedRef(instructorUid, centerId);
  const snap = await getDoc(ref);
  const existing = snap.exists()
    ? new Set((snap.data() as { memberIds?: string[] }).memberIds ?? [])
    : new Set<string>();

  if (existing.has(memberId)) {
    existing.delete(memberId);
  } else {
    existing.add(memberId);
  }

  await setDoc(ref, { memberIds: [...existing] });
  return existing;
}
